import type { HTMLAttributes } from "react";
import { tv, type VariantProps } from "tailwind-variants";

const typography = tv({
  slots: {
    heading: "text-text",
    body: "",
  },
  variants: {
    variant: {
      headingXl: {
        heading: "text-[26px] leading-8 font-semibold tracking-tight",
      },
      headingLg: { heading: "text-xl leading-7 font-semibold tracking-tight" },
      headingMd: { heading: "text-base leading-[22px] font-semibold" },
      headingSm: {
        heading:
          "text-[13px] leading-[18px] font-semibold uppercase tracking-wide",
      },
      bodyLg: { body: "text-[15px] leading-[22px]" },
      bodyMd: { body: "text-sm leading-5" },
      bodySm: { body: "text-xs leading-4" },
    },
    tone: {
      default: { body: "text-text" },
      subdued: { body: "text-subdued" },
      critical: { body: "text-destructive" },
      success: { body: "text-success" },
    },
  },
});

type HeadingVariant = "headingXl" | "headingLg" | "headingMd" | "headingSm";
type BodyVariant = "bodyLg" | "bodyMd" | "bodySm";
type Tone = NonNullable<VariantProps<typeof typography>["tone"]>;

type HeadingProps = HTMLAttributes<HTMLHeadingElement> & {
  as?: "h1" | "h2" | "h3" | "h4" | "h5";
  variant?: HeadingVariant;
};

export const Heading: React.FC<HeadingProps> = ({
  as: As = "h2",
  variant = "headingLg",
  className,
  children,
  ...props
}) => {
  const { heading } = typography({ variant });
  return (
    <As className={heading({ className })} {...props}>
      {children}
    </As>
  );
};

type TextProps = HTMLAttributes<HTMLParagraphElement> & {
  variant?: BodyVariant;
  tone?: Tone;
  as?: "p" | "span" | "div";
};

export const Text: React.FC<TextProps> = ({
  variant = "bodyMd",
  tone = "default",
  as: As = "p",
  className,
  children,
  ...props
}) => {
  const { body } = typography({ variant, tone });
  return (
    <As className={body({ className })} {...props}>
      {children}
    </As>
  );
};
